/**
 * OQ-282 · Thanh tiến trình nhập tên miền, đặt trong header.
 *
 * Chủ dự án có 78 tên miền: *"hộp thoại cứ mở mãi đợi load, điều đó khiến không
 * thao tác được gì khác"*. Nay việc nhập chạy nền, và chỗ này là thứ duy nhất
 * cho biết nó còn đang chạy.
 *
 * ── Vì sao đặt trong header chứ không phải một thẻ nổi ────────────────────
 *
 * Header đã hiện trên **mọi** trang, nên không cần thêm lớp phủ nào để đạt yêu
 * cầu "hiện cho tới khi nhập xong". Một thẻ nổi ở góc thì che nội dung, và thứ
 * che nội dung suốt vài phút sẽ bị người ta tìm cách tắt — rồi mất luôn thông
 * tin ta muốn họ thấy.
 *
 * ── Vì sao hỏi lại theo chu kỳ chứ không mở kênh đẩy ──────────────────────
 *
 * Một câu hỏi nhẹ mỗi 2 giây, và **chỉ khi có việc đang chạy** (`refetchInterval`
 * trả `false` khi rảnh). SSE/WebSocket sẽ đòi thêm một đường sống qua proxy,
 * thêm mã xử lý nối lại, để đổi lấy độ trễ vài giây trên một việc dài vài phút.
 */

import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useT } from '../lib/i18n';
import { Icon } from './Icon';

export interface ImportJob {
  id: string;
  /** `import` = nhập tên miền (OQ-282) · `reconcile` = đối chiếu DNS (OQ-283). */
  kind: 'import' | 'reconcile';
  status: 'queued' | 'running' | 'done' | 'failed' | 'interrupted';
  total: number;
  done: number;
  imported: number;
  skipped: number;
  recordsImported: number;
  error: string | null;
  createdAt: string;
  finishedAt: string | null;
}

const DANG_CHAY = new Set(['queued', 'running']);

export function ImportProgress(): JSX.Element | null {
  const t = useT();
  const queryClient = useQueryClient();

  const { data: job } = useQuery({
    queryKey: ['import-job-active'],
    queryFn: () => api.get<ImportJob | null>('/domains/import/jobs/active'),
    /*
     * Chỉ hỏi lại khi CÒN việc. Trả `false` lúc rảnh nghĩa là mọi phiên đang mở
     * ngừng hẳn — không thì mỗi tab người dùng quên đóng là một dòng request
     * đều đặn suốt ngày, cho một câu trả lời không đổi.
     */
    refetchInterval: (query) => (DANG_CHAY.has(query.state.data?.status ?? '') ? 2000 : false),
    refetchOnWindowFocus: true,
  });

  /*
   * Vừa xong thì làm mới thứ người dùng sắp nhìn. Không có đoạn này, họ thấy
   * "đã nhập 78 tên miền" rồi bấm sang trang Tên miền và gặp danh sách cũ —
   * một mâu thuẫn khiến người ta nghi ngờ cả con số vừa đọc.
   */
  const truoc = useRef<string | undefined>(undefined);
  useEffect(() => {
    const nay = job?.status;
    if (truoc.current && DANG_CHAY.has(truoc.current) && nay && !DANG_CHAY.has(nay)) {
      void queryClient.invalidateQueries({ queryKey: ['domains'] });
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    }
    truoc.current = nay;
  }, [job?.status, queryClient]);

  if (!job || !DANG_CHAY.has(job.status)) return null;

  /* `total = 0` không xảy ra (API chặn danh sách rỗng) nhưng chia cho nó thì ra
     NaN và thanh biến mất — rẻ hơn nhiều so với đi tìm vì sao nó biến mất. */
  const phanTram = job.total > 0 ? Math.round((job.done / job.total) * 100) : 0;

  return (
    <div className="import-progress" data-od-id="import-progress" role="status" aria-live="polite">
      <Icon name="cloud" size={16} />
      <div className="import-progress-body">
        <span className="import-progress-label">
          {/*
            Nhãn theo LOẠI việc, không dùng chung một câu. "Đang nhập" trong khi
            hệ thống đang đối chiếu sẽ làm người dùng chờ một thứ không xảy ra —
            và tệ hơn, tưởng mình vừa vô tình nhập lại 78 tên miền.
          */}
          {t(
            job.kind === 'reconcile'
              ? 'domains.import.progressReconcile'
              : 'domains.import.progress',
            { done: job.done, total: job.total },
          )}
        </span>
        <div
          className="import-progress-track"
          role="progressbar"
          aria-valuenow={job.done}
          aria-valuemin={0}
          aria-valuemax={job.total}
          aria-label={t('domains.import.progressAria')}
        >
          <div className="import-progress-fill" style={{ width: `${phanTram}%` }} />
        </div>
      </div>
    </div>
  );
}
